/**
 * Frontend Validation Rules
 * 
 * This file contains validation rules for form data that mirror the backend validation rules.
 * Used for client-side validation before submitting data.
 */

// US States list with common abbreviations
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC' // District of Columbia
];

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates if a value is a valid US state abbreviation
 */
export function validateUSState(value: any, fieldName: string): ValidationResult {
  const errors: string[] = [];
  
  if (value === null || value === undefined || value === '') {
    return { isValid: true, errors: [] }; // Allow empty values
  }
  
  const stateCode = String(value).trim().toUpperCase();
  
  if (!US_STATES.includes(stateCode)) {
    errors.push(`${fieldName} must be a valid US state abbreviation (e.g., FL, GA, NY). Received: "${value}"`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates if a value is not empty
 */
export function validateRequired(value: any, fieldName: string): ValidationResult {
  const errors: string[] = [];
  
  if (value === null || value === undefined || value === '' || String(value).trim() === '') {
    errors.push(`${fieldName} is required`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates if a value matches a specific pattern (regex)
 */
export function validatePattern(value: any, fieldName: string, pattern: RegExp, errorMessage: string): ValidationResult {
  const errors: string[] = [];
  
  if (value === null || value === undefined || value === '') {
    return { isValid: true, errors: [] }; // Allow empty values
  }
  
  const stringValue = String(value);
  
  if (!pattern.test(stringValue)) {
    errors.push(`${fieldName}: ${errorMessage}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates if a value is within a specific length range
 */
export function validateLength(value: any, fieldName: string, minLength?: number, maxLength?: number): ValidationResult {
  const errors: string[] = [];
  
  if (value === null || value === undefined || value === '') {
    return { isValid: true, errors: [] }; // Allow empty values
  }
  
  const stringValue = String(value);
  
  if (minLength !== undefined && stringValue.length < minLength) {
    errors.push(`${fieldName} must be at least ${minLength} characters long`);
  }
  
  if (maxLength !== undefined && stringValue.length > maxLength) {
    errors.push(`${fieldName} must be no more than ${maxLength} characters long`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates if a value is a valid date
 */
export function validateDate(value: any, fieldName: string): ValidationResult {
  const errors: string[] = [];
  
  if (value === null || value === undefined || value === '') {
    return { isValid: true, errors: [] }; // Allow empty values
  }
  
  let date: Date | null = null;
  
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    date = new Date(value);
  }
  
  if (!date || isNaN(date.getTime())) {
    errors.push(`${fieldName} must be a valid date. Received: "${value}"`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates if a value is a valid VIN (Vehicle Identification Number)
 */
export function validateVIN(value: any, fieldName: string): ValidationResult {
  const errors: string[] = [];
  
  if (value === null || value === undefined || value === '') {
    return { isValid: true, errors: [] }; // Allow empty values
  }
  
  const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/i; // VIN pattern (17 characters, no I, O, Q)
  const stringValue = String(value).trim().toUpperCase();
  
  if (!vinPattern.test(stringValue)) {
    errors.push(`${fieldName} must be a valid 17-character VIN (no I, O, or Q). Received: "${value}"`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Interface for transaction data
 */
export interface TransactionFormData {
  vin?: string | null;
  vendorName?: string | null;
  clientNumber?: string | null;
  unitNumber?: string | null;
  registrationState?: string | null;
  licensePlateState?: string | null;
  transactionType?: string | null;
  requestor?: string | null;
  requestTrackingNumber?: string | null;
  status?: string | null;
  notes?: string | null;
  agencyTrackingNumber?: string | null;
  tempTagNumber?: string | null;
  licensePlateNumber?: string | null;
  invoiceNumber?: string | null;
  requestReceivedAt?: string | null;
  estimatedCompleteAt?: string | null;
  processingCompleteAt?: string | null;
  sentAgencyAt?: string | null;
  tempTagExpirationAt?: string | null;
  licensePlateExpirationAt?: string | null;
}

/**
 * Validates a transaction record form data
 */
export function validateTransactionForm(data: TransactionFormData): ValidationResult {
  const allErrors: string[] = [];
  
  // Required field validations
  const vinValidation = validateRequired(data.vin, 'VIN');
  if (!vinValidation.isValid) {
    allErrors.push(...vinValidation.errors);
  }
  
  // VIN format validation (only if VIN is provided)
  if (data.vin) {
    const vinFormatValidation = validateVIN(data.vin, 'VIN');
    if (!vinFormatValidation.isValid) {
      allErrors.push(...vinFormatValidation.errors);
    }
  }
  
  // US State validations
  if (data.registrationState) {
    const registrationStateValidation = validateUSState(data.registrationState, 'Registration State');
    if (!registrationStateValidation.isValid) {
      allErrors.push(...registrationStateValidation.errors);
    }
  }
  
  if (data.licensePlateState) {
    const licensePlateStateValidation = validateUSState(data.licensePlateState, 'License Plate State');
    if (!licensePlateStateValidation.isValid) {
      allErrors.push(...licensePlateStateValidation.errors);
    }
  }
  
  // Date validations
  if (data.requestReceivedAt) {
    const dateValidation = validateDate(data.requestReceivedAt, 'Request Received Date');
    if (!dateValidation.isValid) {
      allErrors.push(...dateValidation.errors);
    }
  }
  
  if (data.estimatedCompleteAt) {
    const dateValidation = validateDate(data.estimatedCompleteAt, 'Estimated Complete Date');
    if (!dateValidation.isValid) {
      allErrors.push(...dateValidation.errors);
    }
  }
  
  if (data.processingCompleteAt) {
    const dateValidation = validateDate(data.processingCompleteAt, 'Processing Complete Date');
    if (!dateValidation.isValid) {
      allErrors.push(...dateValidation.errors);
    }
  }
  
  if (data.sentAgencyAt) {
    const dateValidation = validateDate(data.sentAgencyAt, 'Sent Agency Date');
    if (!dateValidation.isValid) {
      allErrors.push(...dateValidation.errors);
    }
  }
  
  if (data.tempTagExpirationAt) {
    const dateValidation = validateDate(data.tempTagExpirationAt, 'Temp Tag Expiration Date');
    if (!dateValidation.isValid) {
      allErrors.push(...dateValidation.errors);
    }
  }
  
  if (data.licensePlateExpirationAt) {
    const dateValidation = validateDate(data.licensePlateExpirationAt, 'License Plate Expiration Date');
    if (!dateValidation.isValid) {
      allErrors.push(...dateValidation.errors);
    }
  }
  
  // Length validations
  if (data.clientNumber) {
    const lengthValidation = validateLength(data.clientNumber, 'Client Number', 1, 50);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.unitNumber) {
    const lengthValidation = validateLength(data.unitNumber, 'Unit Number', 1, 50);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.requestTrackingNumber) {
    const lengthValidation = validateLength(data.requestTrackingNumber, 'Request Tracking Number', 1, 100);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.agencyTrackingNumber) {
    const lengthValidation = validateLength(data.agencyTrackingNumber, 'Agency Tracking Number', 1, 100);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.tempTagNumber) {
    const lengthValidation = validateLength(data.tempTagNumber, 'Temp Tag Number', 1, 50);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.licensePlateNumber) {
    const lengthValidation = validateLength(data.licensePlateNumber, 'License Plate Number', 1, 20);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.invoiceNumber) {
    const lengthValidation = validateLength(data.invoiceNumber, 'Invoice Number', 1, 100);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  // Text field validations
  if (data.vendorName) {
    const lengthValidation = validateLength(data.vendorName, 'Vendor Name', 1, 200);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.transactionType) {
    const lengthValidation = validateLength(data.transactionType, 'Transaction Type', 1, 100);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.requestor) {
    const lengthValidation = validateLength(data.requestor, 'Requestor', 1, 200);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.status) {
    const lengthValidation = validateLength(data.status, 'Status', 1, 100);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  if (data.notes) {
    const lengthValidation = validateLength(data.notes, 'Notes', 0, 1000);
    if (!lengthValidation.isValid) {
      allErrors.push(...lengthValidation.errors);
    }
  }
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
}
